import {
  VESSEL_CARD_EVALUATION_FILE_EXTENSIONS,
  VESSEL_CARD_EVALUATION_MAX_FILES,
  VESSEL_CARD_EVALUATION_MAX_FILE_BYTES,
  VESSEL_CARD_EVALUATION_MAX_TOTAL_BYTES,
  VESSEL_CARD_EVALUATION_REASONS,
} from "../constants/vessel-card-evaluation.constants";
import { VesselCardEvaluationNotificationStatus } from "../models/vessel-card-evaluation.models";

// Counts whitespace-separated words, including pasted text, to match backend validation.
export const countVesselCardEvaluationWords = (text: string): number => text.trim() ? text.trim().split(/\s+/).length : 0;

// Rejects the full selection when any file is invalid, keeping the previously accepted files intact.
export const validateVesselCardEvaluationFiles = (files: File[]): string | null => {

  if (files.length > VESSEL_CARD_EVALUATION_MAX_FILES) {
    return "Attach no more than 10 supporting files.";
  }

  for (const file of files) {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

    if (!VESSEL_CARD_EVALUATION_FILE_EXTENSIONS.includes(extension)) {
      return `${file.name}: this file type is not supported.`;
    }

    if (!file.size) {
      return `${file.name}: empty files cannot be attached.`;
    }

    if (file.size > VESSEL_CARD_EVALUATION_MAX_FILE_BYTES) {
      return `${file.name}: each file must be 25 MiB or smaller.`;
    }
  }

  if (files.reduce((total, file) => total + file.size, 0) > VESSEL_CARD_EVALUATION_MAX_TOTAL_BYTES) {
    return "Supporting files must total 50 MiB or less.";
  }

  return null;
};

export const formatVesselCardEvaluationFileSize = (sizeBytes: number): string =>
  sizeBytes < 1024 * 1024 ? `${Math.ceil(sizeBytes / 1024)} KiB` : `${(sizeBytes / (1024 * 1024)).toFixed(1)} MiB`;

export const getVesselCardEvaluationReasonLabel = (reason: string): string =>
  VESSEL_CARD_EVALUATION_REASONS.find((option) => option.value === reason)?.label ?? reason;

// Treats saving and sending a notification as separate outcomes so a saved request is never submitted again just to send email.
export const getVesselCardEvaluationNotificationLabel = (status: VesselCardEvaluationNotificationStatus): string => {

  if (status === "sent") {
    return "Saved; DVP notified.";
  }

  if (status === "failed") {
    return "Saved; DVP notification failed. Contact DVP with the request reference.";
  }

  if (status === "pending") {
    return "Saved; DVP notification is unconfirmed. Contact DVP with the request reference.";
  }

  return "Saved; automatic DVP notification is not configured. Contact DVP with the request reference.";
};
